import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatTableModule } from "@angular/material/table";
import { MatButtonModule } from "@angular/material/button";
import { MatIconModule } from "@angular/material/icon";
import { FeatureBadgeComponent } from "app/components/feature-badge/feature-badge.component";
import { ActivityLogService } from "app/services/activity-log.service";
import { TranslateModule } from "@ngx-translate/core";
import { HighlightLinkSearchPipe, HighlightSearch, MomentPipe, PropsFalsyPipe } from "app/pipes/pipes";
import { Router, RouterModule } from "@angular/router";
import { AnchorWrapPipe, ParseTargetClassPipe, ResolveActivityBadgeStatusPipe } from "./activity-log-table-pipes";
import { ActivityTargetClass, IActivityLog } from "app/models/activity-log.model";
import { Observable } from "rxjs";
import { ScrollNearEndDirective } from "app/directives/scroll-near-end.directive";
import { MatDialog } from "@angular/material/dialog";
import { ActivityDetailsDialogComponent } from "../activity-details-dialog/activity-details-dialog.component";
import { MatTooltipModule } from "@angular/material/tooltip";
import { DevicesComponent } from "app/components/devices/devices.component";
import { LocationNavigationService } from "app/services/location-navigation.service";

@Component({
    selector: "app-activity-log-table",
    imports: [
        CommonModule,
        MatTableModule,
        MatButtonModule,
        MatTooltipModule,
        MatIconModule,
        FeatureBadgeComponent,
        TranslateModule,
        MomentPipe,
        RouterModule,
        AnchorWrapPipe,
        HighlightSearch,
        HighlightLinkSearchPipe,
        ScrollNearEndDirective,
        PropsFalsyPipe,
        ResolveActivityBadgeStatusPipe,
    ],
    templateUrl: "./activity-log-table.component.html",
    changeDetection: ChangeDetectionStrategy.Default,
    styleUrls: ["./activity-log-table.component.css"]
})
export class ActivityLogTableComponent implements OnInit {
  constructor(
    public activityLogService: ActivityLogService,
    public router: Router,
    private dialog: MatDialog,
    private navService: LocationNavigationService
  ) { }

  @ViewChild(DevicesComponent) devicesComponent!: DevicesComponent;

  displayedColumns: string[] = [
    "timestamp",
    "area",
    "user",
    "action",
    "details",
  ];
  activityLogs$: Observable<IActivityLog[]>;

  get scrollDisabled(): boolean {
    return this.activityLogService.infiniteScrollDisabled;
  }

  get searchText(): string {
    return this.activityLogService.filters.search;
  }

  ngOnInit(): void {
    this.activityLogs$ = this.activityLogService.activityLogs$;
  }

  onRowClicked(log: IActivityLog, event: MouseEvent): void {
    const target = event.target as HTMLElement;

    // Don't open the dialog if the user clicked on a link
    if (target.tagName.toLocaleLowerCase() === "a") {
      return;
    }

    // Or the log is a camera group and the action is create
    if (log.details.targetClass === ActivityTargetClass.CameraGroup && log.action.toLocaleLowerCase() === "create") {
      return;
    }

    // Or the log is an authentication log
    if (log.action.toLocaleLowerCase() === "authentication") {
      return;
    }

    // Or the log is key code log
    if (log.action.toLocaleLowerCase() === "keycode") {
      return
    }
    // Or the log is a manual pause-resume 
    
    if (log.area === "Digital Supervision" && ["pause", "resume"].includes(log.action.toLowerCase()) && 
    (
      !(log.details?.translationCode?.includes("SCHEDULED_PAUSE_MODIFIED")) &&
      !(log.details?.translationCode?.includes("SCHEDULED_PAUSE_CREATED"))
    )) {
      return;
    }

    // Or the log is a staff OR AlarmRoute OR AlarmUserGroup log and the action is create or delete
    if ((log.area === "Staff" || log.area === "AlarmRoute" || log.area === "AlarmUserGroup" || log.area === "Location") && ["create", "delete"].includes(log.action.toLocaleLowerCase())) {
      return;
    }

    this.dialog.open(ActivityDetailsDialogComponent, {
      data: log,
      width: "45%",
      position: { top: "5%" },
      disableClose: true,
      autoFocus: false,
    });

  }

  onNearBottomScroll(): void {
    // Don't fetch more logs if we have reached the end of the logs or the previous request is still loading
    if (this.scrollDisabled || this.activityLogService.activityLogsLoading) {
      return;
    }
    this.activityLogService.getNextLogBatch();
  }

  onCellClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
  
    if (target.classList.contains('location-link')) {
      const locationId = target.getAttribute('data-location-id');
      if (!locationId) return;
  
      this.navService.openLocation(locationId);
      this.router.navigate(['/device']);
      return;
    }

    if (target.classList.contains("link")) {
      const url = target.getAttribute("data-url");
      if (url) this.router.navigateByUrl(url);
    }
  }

  // Returns true if the dialog can be opened
  private canOpenDialog(log: IActivityLog, event: MouseEvent): boolean {
    const target = event.target as HTMLElement;

    if (target.tagName.toLocaleLowerCase() === "a") {
      return false;
    }

    // Don't open the dialog if the log is not a scheduled visit and the action is not modify
    if (log.details.targetClass !== ActivityTargetClass.ScheduledVisit && log.action.toLocaleLowerCase() !== "modify") {
      return false;
    }

    return true;
  }

  formatLocalDateTime(value: string): string {
    if (!value) return '';
  
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
  
    const day     = String(d.getDate()).padStart(2, '0');
    const month   = String(d.getMonth() + 1).padStart(2, '0');
    const year    = d.getFullYear();
    const hours   = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
  
    return `${day}.${month}.${year} ${hours}:${minutes}`;
  }
}
